import { useState } from 'react';
import { MailIcon, TrashIcon } from '@/components/catalog/icons';
import { Badge, Button, Combobox, TD, TR } from '@/components/ui';
import type { ComboboxOption } from '@/components/ui';
import { InlineField, type InlineSaveResult } from '@/components/ui/InlineField';
import { Tooltip } from '@/components/ui/Tooltip';
import { primaryContactRefusal } from '@/components/companies/CompanyContactsList';
import { ContactTitleInlineField } from '@/components/contacts/ContactTitleInput';
import { ApiError, api } from '@/lib/api';
import { mailtoHref } from '@/lib/mail';
import type { Contact, ContactGroup, ContactGroupRef, ContactInput } from '@/lib/types';
import { ContactNameCells } from './ContactNameCells';
import {
  INLINE_FIELD_LABELS,
  buildContactPatch,
  fieldValue,
  validateContactDraft,
  type InlineField as InlineFieldKey,
} from './rules';

/*
 * One contact in the contacts manager, as a desktop table row.
 *
 * Cells, in order: Primary | the three name cells (`ContactNameCells`) | Title
 * | Email | Phone | Mobile | Group | Delete. The modal's header must declare the
 * same order; the Delete cell is always rendered (empty without the grant) so
 * the columns never shift between rows.
 *
 * ⚠️ **Two records are written from this row, under two different grants.**
 * Primary is `PATCH /companies/:id {primaryContactId}` (the modal owns it, via
 * `onMakePrimary`, gated on `canEditCompany`); every other cell is
 * `PATCH /contacts/:id` (gated on `canEditContact`). Same split as
 * `CompanyContactsList` — see its header.
 *
 * ⚠️ **The record decides what the row shows.** Every successful write hands
 * the server's contact to `onSaved`, and the modal replaces its copy; nothing
 * here keeps a local copy of a saved value. Only an in-flight or refused group
 * choice overrides the record, so a refusal stays next to the value it refused.
 */

/** Every inline field's value off the record — the base a one-field edit is laid over. */
function draftOf(contact: Contact): Record<InlineFieldKey, string> {
  return {
    name: fieldValue(contact, 'name'),
    firstName: fieldValue(contact, 'firstName'),
    lastName: fieldValue(contact, 'lastName'),
    title: fieldValue(contact, 'title'),
    email: fieldValue(contact, 'email'),
    phone: fieldValue(contact, 'phone'),
    mobile: fieldValue(contact, 'mobile'),
  };
}

/** The refusal in the most specific words available: field → form → message. */
function refusalMessage(error: unknown, field: string | null): string {
  if (error instanceof ApiError) {
    const byField = field === null ? undefined : error.fieldErrors[field];
    return byField ?? error.formMessage ?? error.message;
  }
  return error instanceof Error ? error.message : 'The change could not be saved.';
}

/**
 * The group picker's choices: every ACTIVE group, plus the one this contact
 * already holds when the master no longer offers it — an inactive id is a 422,
 * and a picker rendering blank over a retired group would read as ungrouped.
 * Same rule as `contactGroupOptions` in `CompanyContactsList` (not exported
 * there).
 */
function groupOptions(groups: ContactGroup[], stored: ContactGroupRef | null): ComboboxOption[] {
  const label = (id: number, name: string) => (name.trim() === '' ? `#${id}` : name.trim());
  const options: ComboboxOption[] = groups
    .filter((group) => group.isActive)
    .map((group) => ({ value: String(group.id), label: label(group.id, group.name) }));
  if (!stored || options.some((option) => option.value === String(stored.id))) return options;
  const fresh = groups.find((group) => group.id === stored.id);
  return [{ value: String(stored.id), label: label(stored.id, fresh ? fresh.name : stored.name) }, ...options];
}

type GroupAction =
  | null
  | { kind: 'saving'; value: string }
  | { kind: 'failed'; value: string; message: string };

type PrimaryState = null | { kind: 'saving' } | { kind: 'failed'; message: string };

export interface ContactRowProps {
  contact: Contact;
  groups: ContactGroup[];
  /** From the company's `primaryContactId` — the single source both affordances read. */
  isPrimary: boolean;
  /** `ability.canUpdate('contacts')`: every cell but Primary. */
  canEditContact: boolean;
  /** `ability.canUpdate('companies')`: the Primary cell. */
  canEditCompany: boolean;
  /** `ability.canDelete('contacts')`. */
  canDelete: boolean;
  /** The server's record after any successful contact write. */
  onSaved: (c: Contact) => void;
  /** Writes the company's primary contact. Rejects with the refusal on failure. */
  onMakePrimary: (contactId: number) => Promise<void>;
  /** Opens the modal's one delete confirmation — never deletes by itself. */
  onDelete: (c: Contact) => void;
}

/** Shared by the desktop row and the card: PATCH the contact, hand back the record. */
// oxlint-disable-next-line react/only-export-components
export function useContactRowWrites(
  contact: Contact,
  onSaved: (c: Contact) => void,
  onMakePrimary: (contactId: number) => Promise<void>,
) {
  const [groupAction, setGroupAction] = useState<GroupAction>(null);
  const [primary, setPrimary] = useState<PrimaryState>(null);

  /** One inline field's save, laid over the record so validation sees the whole contact. */
  async function saveField(field: InlineFieldKey, next: string): Promise<InlineSaveResult> {
    const draft = { ...draftOf(contact), [field]: next };
    const refusal = validateContactDraft(draft)[field];
    if (refusal) return { status: 'error', message: refusal };
    const patch = buildContactPatch(contact, draft);
    if (patch === null) return { status: 'saved' };
    try {
      const { data } = await api.updateContact(contact.id, patch);
      onSaved(data);
      return { status: 'saved' };
    } catch (error) {
      return { status: 'error', message: refusalMessage(error, field) };
    }
  }

  /** The name cells' save. Rethrows, so `ContactNameCells` keeps the draft open. */
  async function savePatch(patch: ContactInput): Promise<void> {
    const { data } = await api.updateContact(contact.id, patch);
    onSaved(data);
  }

  async function changeGroup(value: string) {
    const stored = contact.contactGroup ? String(contact.contactGroup.id) : '';
    if (value === stored) {
      setGroupAction(null);
      return;
    }
    setGroupAction({ kind: 'saving', value });
    try {
      // `null` clears the group; `''` would be a 422.
      const { data } = await api.updateContact(contact.id, {
        contactGroupId: value === '' ? null : Number(value),
      });
      setGroupAction(null);
      onSaved(data);
    } catch (error) {
      setGroupAction({ kind: 'failed', value, message: refusalMessage(error, 'contactGroupId') });
    }
  }

  async function makePrimary() {
    setPrimary({ kind: 'saving' });
    try {
      await onMakePrimary(contact.id);
      setPrimary(null);
    } catch (error) {
      setPrimary({ kind: 'failed', message: refusalMessage(error, 'primaryContactId') });
    }
  }

  return { groupAction, primary, saveField, savePatch, changeGroup, makePrimary };
}

/** The Primary cell's contents: the badge on the holder, the toggle on the others. */
export function PrimaryControl({
  contact,
  isPrimary,
  canEditCompany,
  state,
  onMakePrimary,
}: {
  contact: Contact;
  isPrimary: boolean;
  canEditCompany: boolean;
  state: PrimaryState;
  onMakePrimary: () => void;
}) {
  if (isPrimary) {
    return (
      <Badge tone="neutral" variant="outline">
        Primary
      </Badge>
    );
  }
  if (!canEditCompany) return null;
  const refusal = primaryContactRefusal(contact);
  return (
    <div className="flex flex-col items-start gap-1">
      <Button
        size="sm"
        loading={state?.kind === 'saving'}
        aria-label={`Make ${contact.name} the primary contact`}
        onClick={onMakePrimary}
      >
        Make primary
      </Button>
      {/* A refusal, not helper text: it names why the button above comes back 422. */}
      {refusal !== null && <span className="text-2xs text-fg-muted">{refusal}</span>}
      {state?.kind === 'failed' && (
        <span role="alert" className="text-2xs text-danger [overflow-wrap:anywhere]">
          {state.message}
        </span>
      )}
    </div>
  );
}

/** The Group cell's contents. Read-only is plain text, not a disabled picker. */
export function GroupControl({
  contact,
  groups,
  canEditContact,
  action,
  onChange,
}: {
  contact: Contact;
  groups: ContactGroup[];
  canEditContact: boolean;
  action: GroupAction;
  onChange: (value: string) => void;
}) {
  const stored = contact.contactGroup;
  if (!canEditContact) {
    return <span className="text-sm text-fg-secondary">{stored?.name ?? '—'}</span>;
  }
  const options = groupOptions(groups, stored);
  const value = action ? action.value : stored ? String(stored.id) : '';
  return (
    <div role="group" aria-label={`Group for ${contact.name}`} className="grid min-w-0 gap-1">
      <Combobox
        value={value}
        options={options}
        onChange={onChange}
        disabled={action?.kind === 'saving'}
        invalid={action?.kind === 'failed'}
        placeholder="No group"
        emptyOptionLabel="No group"
        searchPlaceholder="Filter groups"
      />
      {action?.kind === 'saving' && <span className="text-2xs text-fg-muted">Saving…</span>}
      {action?.kind === 'failed' && (
        <span role="alert" className="text-2xs text-danger [overflow-wrap:anywhere]">
          {action.message}
        </span>
      )}
    </div>
  );
}

/** The `mailto:` button beside an address (owner, 2026-10-16; the Gmail tab is SUPERSEDED). */
export function EmailComposeButton({ contact, address }: { contact: Contact; address: string }) {
  return (
    <Tooltip label="Email">
      <Button
        href={mailtoHref(address)}
        variant="ghost"
        size="sm"
        iconOnly
        leadingIcon={<MailIcon />}
        aria-label={`Email ${contact.name} at ${address}`}
      />
    </Tooltip>
  );
}

/** One single-line contact field, edited in place. */
export function ContactTextField({
  contact,
  field,
  canEditContact,
  saveField,
}: {
  contact: Contact;
  field: Exclude<InlineFieldKey, 'name' | 'firstName' | 'lastName'>;
  canEditContact: boolean;
  saveField: (field: InlineFieldKey, next: string) => Promise<InlineSaveResult>;
}) {
  const value = fieldValue(contact, field);
  const address = field === 'email' ? value.trim() : '';
  if (field === 'title') {
    return (
      <ContactTitleInlineField
        label={INLINE_FIELD_LABELS.title}
        value={value}
        disabled={!canEditContact}
        onSave={(next) => saveField(field, next)}
        validate={(next) => validateContactDraft({ ...draftOf(contact), [field]: next })[field] ?? null}
      />
    );
  }
  return (
    <InlineField
      label={INLINE_FIELD_LABELS[field]}
      value={value}
      disabled={!canEditContact}
      onSave={(next) => saveField(field, next)}
      validate={(next) => validateContactDraft({ ...draftOf(contact), [field]: next })[field] ?? null}
      {...(field === 'email'
        ? { type: 'email' as const, autoComplete: 'email' }
        : field === 'phone' || field === 'mobile'
          ? { type: 'tel' as const, autoComplete: 'tel' }
          : {})}
      {...(address !== ''
        ? {
            display: <span className="[overflow-wrap:anywhere]">{address}</span>,
            action: <EmailComposeButton contact={contact} address={address} />,
          }
        : {})}
    />
  );
}

/** The Delete cell's contents: icon-only, the name on the accessible label. */
export function DeleteContactButton({
  contact,
  onDelete,
}: {
  contact: Contact;
  onDelete: (c: Contact) => void;
}) {
  return (
    <Tooltip label="Delete">
      <Button
        variant="danger"
        size="sm"
        iconOnly
        leadingIcon={<TrashIcon />}
        aria-label={`Delete ${contact.name}`}
        onClick={() => onDelete(contact)}
      />
    </Tooltip>
  );
}

export function ContactManagerRow({
  contact,
  groups,
  isPrimary,
  canEditContact,
  canEditCompany,
  canDelete,
  onSaved,
  onMakePrimary,
  onDelete,
}: ContactRowProps) {
  const writes = useContactRowWrites(contact, onSaved, onMakePrimary);

  return (
    <TR>
      <TD verticalAlign="top">
        <PrimaryControl
          contact={contact}
          isPrimary={isPrimary}
          canEditCompany={canEditCompany}
          state={writes.primary}
          onMakePrimary={() => void writes.makePrimary()}
        />
      </TD>
      <ContactNameCells contact={contact} canEdit={canEditContact} onSave={writes.savePatch} />
      <TD verticalAlign="top">
        <ContactTextField
          contact={contact}
          field="title"
          canEditContact={canEditContact}
          saveField={writes.saveField}
        />
      </TD>
      <TD verticalAlign="top">
        <ContactTextField
          contact={contact}
          field="email"
          canEditContact={canEditContact}
          saveField={writes.saveField}
        />
      </TD>
      <TD verticalAlign="top">
        <ContactTextField
          contact={contact}
          field="phone"
          canEditContact={canEditContact}
          saveField={writes.saveField}
        />
      </TD>
      <TD verticalAlign="top">
        <ContactTextField
          contact={contact}
          field="mobile"
          canEditContact={canEditContact}
          saveField={writes.saveField}
        />
      </TD>
      <TD verticalAlign="top">
        <GroupControl
          contact={contact}
          groups={groups}
          canEditContact={canEditContact}
          action={writes.groupAction}
          onChange={(value) => void writes.changeGroup(value)}
        />
      </TD>
      <TD verticalAlign="top" numeric>
        {canDelete && <DeleteContactButton contact={contact} onDelete={onDelete} />}
      </TD>
    </TR>
  );
}
